import { useState, useEffect } from 'react';
import { TextField, Box, Grid, Typography, Card, Button, Divider, makeStyles, Chip, Dialog, DialogContent } from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { ArrowBackOutlined, Lock } from '@material-ui/icons'
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useElements, AddressElement, PaymentElement, useStripe } from '@stripe/react-stripe-js';
import useDebounce from '../../../hooks/useDebounce';
import useAuth from '../../../hooks/useAuth';
import { checkCouponCode } from '../../../actions/couponsApi';
import { checkEmailExists } from '../../../actions/userActions';
import { toast } from 'react-hot-toast';
import Bugsnag from '@bugsnag/js';
import { paymentsApi } from '../../../actions/paymentsApi';
import YogaRenewLogin from '../../authentication/YogaRenewLogin';
import useExternalScript from '../../../hooks/useExternalScript';
import { checkSubscriptionValid, checkIAPHubSubscription } from '../../../actions/subscriptionsApi';

const calcDiscount = (price, promo) => {
    if (!promo) return 0
    if (promo.coupon.percent_off) return price * (promo.coupon.percent_off / 100)
    return promo.coupon.amount_off
}

const getPromoDescription = (promo) => {
    if (!promo) return ''
    const duration = promo.coupon.duration_in_months ? `for the first ${promo.coupon.duration_in_months} months` : 'forever'
    if (promo.coupon.percent_off) return `${promo.coupon.percent_off}% off ${duration}`
    return `$${promo.coupon.amount_off / 100} off ${duration}`
}

const getTrialEndDate = (trialDays) => {
    const fmt = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const now = new Date();
    const trialEnd = new Date().setDate(now.getDate() + trialDays);
    return fmt.format(trialEnd);
}

const useStyles = makeStyles((theme) => ({
    form: {
        minHeight: 'inherit'
    }
}))

const emailRegex = /.+\@.+\..+/;

const NewSignup = ({ price, promo }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const classes = useStyles();
    const [logo] = useOutletContext();

    const [newCustomer, setNewCustomer] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState(null);
    const [couponData, setCouponData] = useState(promo);
    const [emailError, setEmailError] = useState(null);
    const [emailInUse, setEmailInUse] = useState(false);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [open, setOpen] = useState(false);
    const [loggedIn, setLoggedIn] = useState(false);
    const [referralCode, setReferralCode] = useState(new URLSearchParams(window.location.search).get('referralCode'));
    const [rewardfulReferralId, setRewardfulReferralId] = useState(localStorage.getItem('rewardfulReferralId'));
    const [taxEstimate, setTaxEstimate] = useState(null);
    const [address, setAddress] = useState(null);

    useExternalScript('https://www.dwin1.com/81255.js', 'head');

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const debouncedCouponCode = useDebounce(couponCode, 500);
    const debouncdEmail = useDebounce(email, 500);

    const elements = useElements();
    const stripe = useStripe();

    const handleAddressChange = (event) => {
        if (event.value.address && event.value.address.postal_code) {
            setAddress(event.value.address)
        }
        if (event.complete) {
            // Extract the first and last names from the AddressElement
            const nameFields = event.value;
            if (nameFields) {
                setFirstName(nameFields.firstName);
                setLastName(nameFields.lastName);
            }

            if (event.value.address !== address) {
                setAddress(event.value.address)
            }
        }
    };

    const checkSubscription = async () => {
        if (!user) return
        if (!user.stripesubscriptionid && !user.iaphubpurchaseid) return

        if (user.iaphubpurchaseid) {
            const res = await checkIAPHubSubscription(user.iaphubpurchaseid)
            if (res.valid) {
                navigate('/yogarenew')
            }
        }

        if (user.stripesubscriptionid) {
            const res = await checkSubscriptionValid(user.stripesubscriptionid)
            if (res.valid) {
                navigate('/yogarenew')
            }
        }
    }

    useEffect(() => {
        const MAX_ATTEMPTS = 50;
        let attempts = 0;

        const checkRewardful = () => {
            attempts++;

            if (typeof window.rewardful === 'function') {
                window.rewardful('ready', function () {
                    const referralId = window.Rewardful.referral;
                    const storedReferralId = localStorage.getItem('rewardfulReferralId');
                    setRewardfulReferralId(referralId);

                    if (referralId && !storedReferralId) {
                        localStorage.setItem('rewardfulReferralId', referralId);
                    }
                });
            } else if (attempts < MAX_ATTEMPTS) {
                setTimeout(checkRewardful, 100);
            } else {
                console.warn("Failed to initialize rewardful after multiple attempts");
            }
        };

        checkRewardful();
    }, []);

    useEffect(() => {
        if (!promo) return
        setCouponCode(promo.code)
        setCouponData(promo)
    }, [promo])

    useEffect(() => {
        if (!user) {
            setNewCustomer(true)
            return
        }

        checkSubscription()

        if (!user.stripecustomerid) {
            setNewCustomer(true)
        }

        if (user.stripecustomerid) {
            paymentsApi.checkTrialEligibility({ customerId: user.stripecustomerid, priceId: price.id })
                .then(res => {
                    setNewCustomer(res.eligible)
                    console.log("New customer: ", res.eligible)
                })
        }

        setEmail(user.email)
    }, [user])

    useEffect(() => {
        if (!debouncdEmail) return
        const checkEmail = async () => {
            if (emailRegex.test(debouncdEmail) === false) return
            const res = await checkEmailExists(debouncdEmail)
            if (res.used) {
                setEmailError('This email is already in use')
                setEmailInUse(true)
            } else {
                setEmailError(null)
                setEmailInUse(false)
            }
        }
        if (user) return
        checkEmail()
    }, [debouncdEmail])

    useEffect(() => {
        if (!debouncedCouponCode) return
        const abort = new AbortController();
        const checkCoupon = async () => {
            const res = await checkCouponCode(debouncedCouponCode, abort.signal)
            console.log(res.valid)

            if (res.valid) {
                setCouponError(null)
                setCouponData(res.coupon)
            } else {
                setCouponError('Invalid coupon code')
            }
        }
        checkCoupon()

        return () => abort.abort()
    }, [debouncedCouponCode])

    useEffect(() => {
        if (!address) return
        const calculateTax = async () => {
            const res = await paymentsApi.calculateTax(price, address, couponData)
            setTaxEstimate((res.tax_breakdown[0].amount / 100).toFixed(2))
        }
        calculateTax()
    }, [address, price, couponData])

    const onEmailBlur = async (e) => {
        const valid = emailRegex.test(email)
        const res = await checkEmailExists(email)
        const typo = email.endsWith('.con')

        if (!valid) {
            setEmailError('Please enter a valid email address')
            return
        }
        if (res.used) {
            setEmailError('This email is already in use')
            setEmailInUse(true)
            return
        }

        if (typo) {
            setEmailError('Did you mean .com?')
            return

        }

        setEmailError(null)
        setEmailInUse(false)
    }

    const completeSubscription = async (setupIntent) => {
        // This requires authenticaion
        // If the user had to set their password, they should be logged in now
        // If the user had an account, they should have been logged in before getting to this page
        const res = await paymentsApi.completeCheckout({
            setupIntentId: setupIntent.id,
            clientSecret: setupIntent.client_secret,
            price: price.id,
            promo: promo?.id || couponData?.id,
        })

        console.log("Complete response", res)

        if (!res.subscription) {
            toast.error(res.msg)
            return false
        }

        return res.subscription.latest_invoice
    }

    const processSetup = async () => {
        if (!user && !email) return setEmailError('Please enter an email address')
        if (!user && emailRegex.test(email) === false) return setEmailError('Please enter a valid email address')
        if (!user && emailInUse) return setEmailError('This email is already in use')

        // Get return URL incase stripe needs to redirect
        const returnUrl = window.location.href;

        const setupResult = await stripe.confirmSetup({ elements, redirect: 'if_required', confirmParams: { return_url: returnUrl } });

        console.log(setupResult)
        // If there was an error, display it. This would normally be due to a card error.
        if (setupResult?.error) {
            // If we accidentally confirmed twice, Stripe returns this special error.
            if (
                setupResult.error.code === 'setup_intent_unexpected_state' &&
                setupResult.error.setup_intent?.status === 'succeeded'
            ) {
                // Treat as success and continue.
            } else {
                toast.error(setupResult.error.message);
                return;
            }
        }

        if (setupResult?.setupIntent?.status !== 'succeeded') {
            toast.error('There was an error setting up your account. Please try again later.')
            Bugsnag.notify(new Error("Setup intent returned unhandled status " + JSON.stringify(setupResult.setupIntent)))
            return
        }

        // If we get here, the setup intent was successful.

        // Prepare the customer account for checkout (backend process)
        const result = await paymentsApi.setupCheckout({
            setupIntentId: setupResult.setupIntent.id,
            clientSecret: setupResult.setupIntent.client_secret,
            email: email,
            firstName: firstName,
            lastName: lastName,
            referralCode: referralCode,
            rewardfulReferralId: rewardfulReferralId
        })

        console.log("Setup Checkout Result", result)
        if (result.jwt) {
            // Set the JWT in local storage
            localStorage.setItem('accessToken', result.jwt)
        }

        const latestInvoice = await completeSubscription(setupResult.setupIntent)

        console.log("Latest Invoice", latestInvoice)

        if (latestInvoice) {

            // Navigate to the complete checkout page
            navigate('/yogarenew/signup/complete', {
                state: {
                    price,
                    promo: couponData,
                    user: result.user,
                    token: result.token,
                    setupIntent: setupResult.setupIntent,
                    loggedIn: user ? true : loggedIn,
                    invoiceId: latestInvoice
                }
            })
        } else {
            toast.error('There was an error completing your subscription. Please try again later.')
            Bugsnag.notify(new Error("Error completing subscription"))
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        await processSetup();
        setLoading(false);
    }

    const subtotal = ((price.unit_amount - (couponData ? calcDiscount(price.unit_amount, couponData) : 0)) / 100).toFixed(2)
    const total = (Number(subtotal) + Number(taxEstimate || 0)).toFixed(2)

    return (
        <form className={classes.form} onSubmit={(e) => handleSubmit(e).finally(() => setLoading(false))}>
            <Grid container sx={{ minHeight: 'inherit' }}>
                <Grid item xs={12} lg={4} sx={{ backgroundColor: '#D9D9D9', px: 3, py: 2 }}>
                    <Card sx={{ px: 4, py: 3, mt: 2 }}>
                        {
                            !user
                                ? <>
                                    <Typography variant="h6" fontWeight={"bold"} sx={{ mb: 1 }}>
                                        Get Started
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Please provide an email address that allows us to send you everything you need.
                                    </Typography>
                                    <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                        Email Address
                                    </Typography>
                                    <TextField
                                        variant="outlined"
                                        name="email"
                                        fullWidth
                                        size='small'
                                        onChange={(e) => setEmail(e.target.value)}
                                        error={emailError !== null}
                                        helperText={emailError === null ? ' ' : emailError}
                                        onBlur={onEmailBlur}
                                        onKeyUp={(e) => { if (e.key === 'Enter') onEmailBlur() }}
                                        required
                                    />
                                </>
                                : <>
                                    <Typography variant="h6" fontWeight={"bold"} sx={{ mb: 1 }}>
                                        Welcome back, {user.firstname || user.username}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        You are already logged in. If you would like to use a different email address, please log out first.
                                    </Typography>
                                </>
                        }
                        {
                            !user && emailInUse
                                ?
                                <>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        This email is already in use. If you already have an account:&nbsp;
                                    </Typography>
                                    <Button onClick={handleOpen} sx={{ mt: 2 }} variant='contained'>Log in here
                                    </Button>
                                </>
                                : null
                        }
                    </Card>
                    <Card sx={{ px: 4, py: 3, mt: 1 }}>
                        <Typography variant="h6" fontWeight={"bold"} sx={{ mb: 1 }}>
                            Payment Information
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Transactions are secure and encrypted.
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <AddressElement
                                onChange={handleAddressChange}
                                options={{
                                    mode: "billing",
                                    defaultValues: {
                                        firstName: user ? user.firstName : undefined,
                                        lastName: user ? user.lastName : undefined,
                                    },
                                    display: {
                                        name: 'split'
                                    }
                                }} />
                        </Box>
                        <Box >
                            <PaymentElement />
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} lg={8}>
                    <Box sx={{ display: 'flex', mx: 5, mt: 7, flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}>
                            <Typography variant="h6" fontWeight={"bold"} sx={{ mb: 1 }}>
                                Order Summary
                            </Typography>
                            <Button startIcon={<ArrowBackOutlined />} onClick={() => navigate('/yogarenew/subscriptions')}>
                                Back To Plans
                            </Button>
                        </Box>
                        <Card sx={{ backgroundColor: 'transparent', border: '1px solid #A7A7A7', mt: 2, px: 4, py: 3 }}>
                            <Grid container spacing={4}>
                                <Grid item lg={2} md={4} xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <img alt="Logo" src={logo} width="100%" height="fit-content" />
                                </Grid>
                                <Grid item lg={10} md={8} xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                            Subscription: {price.nickname}
                                        </Typography>
                                        <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                            ${(price.unit_amount / 100).toFixed(2)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                        Have a coupon code?
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Box>
                                            {
                                                couponData === null
                                                    ? <TextField
                                                        value={couponCode}
                                                        variant="outlined"
                                                        name="coupon"
                                                        sx={{ maxWidth: '180px' }}
                                                        fullWidth
                                                        size='small'
                                                        placeholder='Coupon Code'
                                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                        error={couponError !== null}
                                                        helperText={couponError}
                                                    />
                                                    : <Chip label={couponData.code} onDelete={() => { setCouponData(null); setCouponCode('') }} />
                                            }
                                            <Typography>{getPromoDescription(couponData)}</Typography>
                                            {couponCode === "ONEYEARFREE" && couponData && <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }} fontSize="0.75rem">
                                                You get a one-year free trial with this code. You will not be charged until the trial ends.
                                            </Typography>}
                                        </Box>
                                        <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                            {couponData ? "-$" + (calcDiscount(price.unit_amount, couponData) / 100).toFixed(2) : "-"}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                        Have a referral code?
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <TextField
                                            variant="outlined"
                                            name="referralCode"
                                            fullWidth
                                            size='small'
                                            placeholder='Referral Code'
                                            sx={{ maxWidth: '180px' }}
                                            value={referralCode}
                                            onChange={(e) => setReferralCode(e.target.value)}
                                        />
                                    </Box>
                                    <Divider sx={{ mb: 3 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Box sx={{ width: '60%' }}>
                                            <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                                Subtotal
                                            </Typography>
                                            <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                                Estimated Tax
                                            </Typography>
                                            <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1 }}>
                                                Total{newCustomer ? "*" : null} (USD)
                                            </Typography>
                                            {
                                                newCustomer && couponCode !== "ONEYEARFREE" &&
                                                <Typography variant="subtitle1" sx={{ mt: 2 }} fontSize="0.75rem">
                                                    *{price.nickname} subscription of ${total} will be charged on {getTrialEndDate(7)}. You may cancel anytime beforehand.
                                                </Typography>
                                            }
                                            {
                                                couponCode === "ONEYEARFREE" && couponData &&
                                                <Typography variant="subtitle1" sx={{ mt: 2 }} fontSize="0.75rem">
                                                    *{price.nickname} subscription of ${total} will be charged on {getTrialEndDate(365)}. You may cancel anytime beforehand.
                                                </Typography>
                                            }
                                        </Box>
                                        <Box sx={{ width: '40%' }}>
                                            <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1, textAlign: 'right' }}>
                                                ${subtotal}
                                            </Typography>
                                            <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1, textAlign: 'right' }}>
                                                {taxEstimate ? `$${Number(taxEstimate).toFixed(2)}` : '--'}
                                            </Typography>
                                            <Typography variant="body1" fontWeight={"bold"} sx={{ mb: 1, textAlign: 'right' }}>
                                                ${total}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Card>
                        <Grid container sx={{ mb: 4 }}>
                            <Grid item xs={12} md={10}>
                                <Box sx={{ display: 'flex', pt: 3 }}>
                                    <Lock /><Typography variant='body2' fontWeight='bold' sx={{ lineHeight: 2.25 }}>Payment secured with Stripe</Typography>
                                </Box>
                                <Typography variant='body2' sx={{ pt: 1 }}>
                                    By completing this purchase, I agree to YogaRenew’s <a href="/yogarenew/termsofuse" target='_blank' style={{ color: 'inherit' }}>Terms of Use</a>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={2} sx={{ justifyContent: 'right', display: 'flex' }}>
                                <LoadingButton
                                    variant="contained"
                                    sx={{ mt: 3, height: 50 }}
                                    disabled={Boolean(emailError)}
                                    loading={loading}
                                    type="submit"
                                >
                                    SUBSCRIBE NOW
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
                <Dialog open={open} onClose={handleClose}>
                    <DialogContent>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button onClick={handleClose}>X</Button>
                        </Box>
                        <YogaRenewLogin setOpen={setOpen} email={email} setEmailError={setEmailError} setLoggedIn={setLoggedIn} />
                    </DialogContent>
                </Dialog>
            </Grid>
        </form >
    );
}

export default NewSignup;